import {
  App,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Input,
  List,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import {
  PROJECT_REPORT_PERIOD_LABELS,
  PROJECT_REPORT_STATUS_LABELS,
  projectReportStatusTagColor,
} from '@/constants/project-report';
import { useProjectReportDetail } from '@/hooks/useProjectReportDetail';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import { useAuthStore } from '@/stores/auth.store';
import { canCommentProjectReport, canDeleteProjectReport } from '@/utils/project-report-permissions';
import { formatDate, formatDateTime } from '@/utils/format-date';

interface ProjectReportDetailDrawerProps {
  open: boolean;
  reportId: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

export function ProjectReportDetailDrawer({
  open,
  reportId,
  onClose,
  onChanged,
}: ProjectReportDetailDrawerProps) {
  const { message, modal } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);
  const { loading, report, comments, createComment } =
    useProjectReportDetail(open ? reportId ?? undefined : undefined);

  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const reportTypeLabel = report
    ? `项目${PROJECT_REPORT_PERIOD_LABELS[report.reportPeriod]}`
    : '项目进展';

  useEffect(() => {
    if (!open) {
      setCommentText('');
    }
  }, [open]);

  const handleComment = async () => {
    const content = commentText.trim();
    if (!content) {
      message.warning('请填写点评内容');
      return;
    }

    setSubmitting(true);
    try {
      await createComment(content);
      setCommentText('');
      onChanged?.();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '点评失败';
      message.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!reportId || !report) {
      return;
    }
    if (!canDeleteProjectReport(currentUser, report)) {
      return;
    }

    modal.confirm({
      title: `确认删除该${reportTypeLabel}？`,
      content: '删除后不可恢复（软删除）。',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        await projectReportsApi.deleteProjectReport(reportId);
        message.success(`${reportTypeLabel}已删除`);
        onClose();
        onChanged?.();
      },
    });
  };

  const showCommentBox = canCommentProjectReport(currentUser, report);

  return (
    <Drawer title={`${reportTypeLabel}详情`} open={open} onClose={onClose} width={640} destroyOnClose>
      <Spin spinning={loading}>
        {report ? (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="项目">
                {report.project.name}（{report.project.projectNo}）
              </Descriptions.Item>
              <Descriptions.Item label="提交人">{report.createdBy.name}</Descriptions.Item>
              <Descriptions.Item label="周期">
                {PROJECT_REPORT_PERIOD_LABELS[report.reportPeriod]}
              </Descriptions.Item>
              <Descriptions.Item label="汇报日期">{formatDate(report.reportDate)}</Descriptions.Item>
              <Descriptions.Item label="进度">{report.progressPercent}%</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={projectReportStatusTagColor(report.status)}>
                  {PROJECT_REPORT_STATUS_LABELS[report.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="进展说明">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.progressContent}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="存在问题">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.problemContent || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="下一步计划">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.nextPlanContent || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">点评记录（{comments.length}）</Divider>
            <List
              dataSource={comments}
              locale={{ emptyText: '暂无点评' }}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={`${item.author.name} · ${formatDateTime(item.createdAt)}`}
                    description={
                      <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {item.content}
                      </Typography.Paragraph>
                    }
                  />
                </List.Item>
              )}
            />

            {showCommentBox ? (
              <>
                <Divider orientation="left">发表点评</Divider>
                <Input.TextArea
                  rows={4}
                  maxLength={5000}
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  placeholder="请输入点评内容"
                />
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Button type="primary" loading={submitting} onClick={() => void handleComment()}>
                    提交点评
                  </Button>
                </div>
              </>
            ) : null}

            {canDeleteProjectReport(currentUser, report) ? (
              <div style={{ marginTop: 24 }}>
                <Button danger onClick={handleDelete}>
                  删除{reportTypeLabel}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Spin>
    </Drawer>
  );
}
